/**
 * classify — pure helper mapping an asset filename to its broad media kind.
 *
 * Used by the asset workspace to decide whether a card shows an image
 * thumbnail or a type glyph + extension label. Kept as a plain .ts module
 * (no React) so it has its own fast node-env unit test.
 */

export type AssetKind = "image" | "audio" | "video" | "font" | "other";

/** Extension (sans dot, lowercase) → kind. Mirrors App.tsx's IMAGE_ASSET_RE for images. */
const EXT_KIND: Readonly<Record<string, AssetKind>> = {
  // image
  png: "image",
  jpg: "image",
  jpeg: "image",
  webp: "image",
  gif: "image",
  svg: "image",
  // audio
  mp3: "audio",
  wav: "audio",
  ogg: "audio",
  m4a: "audio",
  // video
  mp4: "video",
  webm: "video",
  mov: "video",
  // font
  ttf: "font",
  otf: "font",
  woff: "font",
  woff2: "font",
};

/**
 * Classify an asset by its filename extension. Case-insensitive; anything with
 * no extension or an unknown extension falls back to `"other"`.
 */
export function classifyAsset(filename: string): AssetKind {
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) return "other";
  const ext = filename.slice(dot + 1).toLowerCase();
  return EXT_KIND[ext] ?? "other";
}

/** Uppercase extension label for non-image cards (e.g. "MP3"); `""` when none. */
export function extensionLabel(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) return "";
  return filename.slice(dot + 1).toUpperCase();
}
